/**
 * Intake call guide (Topics step): a single-column, section-ordered conversation
 * guide where each section is a bordered card holding its clinically-approved
 * talk track (purpose, suggested questions, follow-up/conditional groups,
 * listen-for cues) and, for intake encounters, a scratch input separated by a
 * divider. Talk-track content is the clinical content of record and is rendered
 * verbatim from `INTAKE_TALK_TRACKS` (D2/D12/D13). Scratch is captured here
 * (during the call) and surfaced read-only on the Notes step via a hover icon;
 * it sits alongside the talk tracks (D14). Section order + pediatric gating come
 * from the shared `NOTE_SECTIONS` so the guide, the scratch keys, and the Notes
 * step all agree.
 * @agentic connections
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Group,
  List,
  Paper,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { FormSection } from "@benchmark/ui/components/atomic";
import { isIntakeEncounterType } from "@benchmark/core/constants";

import { NOTE_SECTIONS } from "../note-sections";
import { NoteSectionAudienceBadge } from "../NoteSectionAudienceBadge";
import {
  GOALS_TALK_TRACK,
  INTAKE_TALK_TRACKS,
  type TalkTrack,
} from "../intake-talk-tracks";
import { IntakeGuideWayfinding } from "./IntakeGuideWayfinding";

/**
 * Renders a section's talk track verbatim: suggested questions, follow-up/
 * conditional groups (safety-sensitive ones visually distinguished), and
 * scannable listen-for cues. The purpose line is rendered by the card so it
 * stays visible when the section is collapsed. Presentation only — no capture
 * (D1).
 * @agentic connections
 */
const TalkTrackBody = ({
  track,
  sectionId,
}: {
  track: TalkTrack;
  sectionId: string;
}) => (
  <Stack gap="sm">
    {track.questions && track.questions.length > 0 && (
      <List size="sm" spacing={4} withPadding>
        {track.questions.map((q) => (
          <List.Item key={q}>{q}</List.Item>
        ))}
      </List>
    )}

    {track.groups?.map((group) => {
      const body = (
        <Stack gap={4}>
          {/* Group labels are approved clinical content (Appendix A) — render
              verbatim, without a case transform (D12). */}
          <Text fw={600} size="sm" c="gray.7">
            {group.label}
          </Text>
          {group.questions && group.questions.length > 0 && (
            <List size="sm" spacing={4} withPadding>
              {group.questions.map((q) => (
                <List.Item key={q}>{q}</List.Item>
              ))}
            </List>
          )}
          {group.note && (
            <Text size="sm" c="gray.7" fs="italic">
              {group.note}
            </Text>
          )}
        </Stack>
      );

      return group.safety ? (
        <Box
          key={group.label}
          data-testid={`talk-track-safety-group-${sectionId}`}
          style={{
            borderLeft: "3px solid var(--mantine-color-orange-6)",
            paddingLeft: "var(--mantine-spacing-sm)",
          }}
        >
          {body}
        </Box>
      ) : (
        <Box key={group.label}>{body}</Box>
      );
    })}

    {track.note && (
      <Text
        size="sm"
        c="gray.7"
        fs="italic"
        data-testid={`talk-track-note-${sectionId}`}
      >
        {track.note}
      </Text>
    )}

    {track.listenFors && track.listenFors.length > 0 && (
      <Group gap="xs" align="center">
        <Text fw={600} size="xs" tt="uppercase" c="gray.6">
          Listen for
        </Text>
        <Group gap={6}>
          {track.listenFors.map((cue) => (
            <Badge key={cue} size="sm" radius="sm" variant="light" color="gray">
              {cue}
            </Badge>
          ))}
        </Group>
      </Group>
    )}
  </Stack>
);

/**
 * Props for intake guide section.
 * @agentic connections
 */
export type IntakeGuideSectionProps = {
  encounterType: string;
  /** When true, the patient is under 18 and pediatric sections show (D3). */
  isPediatric?: boolean;
  /** Per-section scratch notes, keyed by note-section id (draft-only). */
  sectionScratch?: Record<string, string>;
  /** Called when a section's scratch note changes. */
  onSectionScratchChange?: (sectionId: string, value: string) => void;
  /** General (whole-call) scratch notes, persisted on the encounter. */
  generalScratch?: string;
  /** Called when the general scratch note changes. */
  onGeneralScratchChange?: (value: string) => void;
  /**
   * Goal-capture UI rendered inside the guide as a Goals card immediately before
   * Assessment & Plan (amendment §8.5, D18). When provided, the guide also adds a
   * Goals wayfinding entry. This is a **surface relocation** of the existing
   * standalone Goals step — the capture behavior and persistence are unchanged
   * (the caller passes the same `GoalCreationSection` bound to the form).
   */
  goalsSlot?: ReactNode;
};

/**
 * Single-column intake call guide: per-section talk tracks with scratch.
 * @agentic connections
 */
export const IntakeGuideSection = ({
  encounterType,
  isPediatric = false,
  sectionScratch = {},
  onSectionScratchChange,
  generalScratch,
  onGeneralScratchChange,
  goalsSlot,
}: IntakeGuideSectionProps) => {
  const isIntake = isIntakeEncounterType(encounterType);
  // Correct order + conditional pediatric sections from the shared list.
  // Pediatric sections show only for minors; the adult Social History section
  // is hidden for minors (R2) — its pediatric counterpart replaces it — unless
  // a pre-fix draft already captured scratch under the adult section, which
  // must stay reachable rather than vanish on resume.
  const guideSections = NOTE_SECTIONS.filter((s) => {
    if (s.pediatric && !isPediatric) return false;
    if (
      s.audience === "adult" &&
      isPediatric &&
      !sectionScratch[s.id]?.trim()
    ) {
      return false;
    }
    return true;
  });

  // Collapse state is in-memory only and defaults to expanded (an absent entry
  // reads as expanded), so a refresh mid-guide reopens everything (AC10). Keyed
  // by section id.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const isExpanded = (id: string) => collapsed[id] !== true;
  const toggleSection = (id: string) =>
    setCollapsed((prev) => ({ ...prev, [id]: !(prev[id] ?? false) }));
  // Live-call wayfinding: the rail + scroll-spy order. Goals is NOT a
  // NOTE_SECTIONS section (it persists as FHIR Goals, not impression fields), so
  // when a Goals card is present it is inserted as a guide-local nav entry
  // immediately before Assessment & Plan (D21/§8.5.1).
  const showGoals = goalsSlot !== undefined;
  const guideSectionKey = guideSections.map((s) => s.id).join(",");
  const navEntries = useMemo(() => {
    const entries: {
      id: string;
      label: string;
      audience?: "adult" | "pediatric";
    }[] = [];
    for (const s of guideSections) {
      if (showGoals && s.id === "assessment-plan") {
        entries.push({ id: "goals", label: "Goals" });
      }
      entries.push({ id: s.id, label: s.label, audience: s.audience });
    }
    return entries;
    // guideSectionKey captures the visible-section set (adult vs pediatric).
  }, [guideSectionKey, showGoals]);
  const navKey = navEntries.map((e) => e.id).join(",");

  const setAll = (value: boolean) =>
    setCollapsed(Object.fromEntries(navEntries.map((e) => [e.id, value])));

  // Refs are keyed by nav-entry id (incl. "goals"); the observer picks the
  // top-most intersecting card.
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeSectionId, setActiveSectionId] = useState<string | undefined>(
    guideSections[0]?.id,
  );

  useEffect(() => {
    // jsdom / unsupported environments: degrade to jump-only (no highlight).
    if (typeof IntersectionObserver === "undefined") return;
    const orderedIds = navKey.split(",");
    // Track ALL currently-intersecting sections across callbacks. A callback
    // only reports the cards whose intersection just changed, so choosing from
    // `entries` alone would ignore a higher card that is still in view; the
    // running set fixes that. The active section is the first intersecting card
    // in display order (i.e. the top-most one on screen).
    const intersecting = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.getAttribute("data-section-id");
          if (!id) continue;
          if (entry.isIntersecting) intersecting.add(id);
          else intersecting.delete(id);
        }
        const active = orderedIds.find((id) => intersecting.has(id));
        if (active) setActiveSectionId(active);
      },
      // Bias toward the section near the top of the viewport.
      { rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );
    for (const id of orderedIds) {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
    // Re-observe when the set of visible entries changes (adult vs pediatric,
    // and whether the Goals card is present).
  }, [navKey]);

  // Sections with notes = non-empty per-section scratch. Drives the wayfinding
  // notes-progress cue (§8.1.3). The whole-call scratch pad is not attributed to
  // any section and does not contribute.
  const sectionsWithNotes = new Set(
    Object.entries(sectionScratch)
      .filter(([, value]) => value?.trim())
      .map(([id]) => id),
  );

  const jumpToSection = (id: string) => {
    // Expand a collapsed target so the section header lands in view (§8.1.4).
    setCollapsed((prev) => ({ ...prev, [id]: false }));
    sectionRefs.current[id]?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
    setActiveSectionId(id);
  };

  return (
    <FormSection
      title="Encounter Guide"
      subtitle="Use these talk tracks to guide the conversation, and jot scratch notes per section as you go. Scratch is your reference (surfaced on hover on the Notes step): it is saved to this encounter, but not shown in the member's health record or the note copy/paste."
      showDivider={false}
    >
      <Group align="flex-start" wrap="nowrap" gap="lg" mt="sm">
        <IntakeGuideWayfinding
          sections={navEntries}
          activeSectionId={activeSectionId}
          sectionsWithNotes={sectionsWithNotes}
          onJump={jumpToSection}
          generalScratch={generalScratch}
          onGeneralScratchChange={isIntake ? onGeneralScratchChange : undefined}
        />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              size="compact-xs"
              onClick={() => setAll(false)}
              data-testid="guide-expand-all"
            >
              Expand all
            </Button>
            <Button
              variant="subtle"
              size="compact-xs"
              onClick={() => setAll(true)}
              data-testid="guide-collapse-all"
            >
              Collapse all
            </Button>
          </Group>
          <Stack gap="lg" mt="sm">
            {navEntries.map((entry) => {
              const expanded = isExpanded(entry.id);

              // Guide-local Goals card (before Assessment & Plan, §8.5): the
              // relocated goal-capture UI (goalsSlot) opened by the miracle-
              // question talk track and the SMART note. Not a NOTE_SECTIONS
              // section — no per-section scratch (D18/D21).
              if (entry.id === "goals") {
                return (
                  <Paper
                    key="goals"
                    withBorder
                    radius="md"
                    p="md"
                    data-section-id="goals"
                    ref={(el) => {
                      sectionRefs.current["goals"] = el;
                    }}
                  >
                    <Group
                      gap="xs"
                      align="center"
                      wrap="nowrap"
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleSection("goals")}
                      data-testid="section-toggle-goals"
                    >
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label={`${expanded ? "Collapse" : "Expand"} Goals`}
                        aria-expanded={expanded}
                      >
                        {expanded ? (
                          <IconChevronDown size={16} />
                        ) : (
                          <IconChevronRight size={16} />
                        )}
                      </ActionIcon>
                      <Text fw={700} size="sm" tt="uppercase" c="gray.7">
                        {entry.label}
                      </Text>
                    </Group>

                    <Text size="sm" c="gray.7" fs="italic" mt={4} ml={28}>
                      {GOALS_TALK_TRACK.purpose}
                    </Text>

                    <Collapse in={expanded}>
                      <Stack gap="md" mt="sm">
                        <TalkTrackBody
                          track={GOALS_TALK_TRACK}
                          sectionId="goals"
                        />
                        <Divider mt="xs" />
                        {goalsSlot}
                      </Stack>
                    </Collapse>
                  </Paper>
                );
              }

              const track = INTAKE_TALK_TRACKS[entry.id];

              return (
                <Paper
                  key={entry.id}
                  withBorder
                  radius="md"
                  p="md"
                  data-section-id={entry.id}
                  ref={(el) => {
                    sectionRefs.current[entry.id] = el;
                  }}
                >
                  <Group
                    gap="xs"
                    align="center"
                    wrap="nowrap"
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleSection(entry.id)}
                    data-testid={`section-toggle-${entry.id}`}
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={`${expanded ? "Collapse" : "Expand"} ${entry.label}`}
                      aria-expanded={expanded}
                    >
                      {expanded ? (
                        <IconChevronDown size={16} />
                      ) : (
                        <IconChevronRight size={16} />
                      )}
                    </ActionIcon>
                    <Text fw={700} size="sm" tt="uppercase" c="gray.7">
                      {entry.label}
                    </Text>
                    {entry.audience && (
                      <NoteSectionAudienceBadge audience={entry.audience} />
                    )}
                  </Group>

                  {/* Purpose stays visible when collapsed so the clinician can skim
                  the whole call from purpose lines alone (AC2). */}
                  {track && (
                    <Text size="sm" c="gray.7" fs="italic" mt={4} ml={28}>
                      {track.purpose}
                    </Text>
                  )}

                  <Collapse in={expanded}>
                    <Stack gap="md" mt="sm">
                      {track && (
                        <TalkTrackBody track={track} sectionId={entry.id} />
                      )}
                      {isIntake && (
                        <>
                          <Divider mt="xs" />
                          <Textarea
                            label="Scratch notes"
                            placeholder="Working notes for this section — saved to this encounter, but not shown in the member's health record or the note copy/paste"
                            value={sectionScratch[entry.id] || ""}
                            onChange={(e) =>
                              onSectionScratchChange?.(
                                entry.id,
                                e.currentTarget.value,
                              )
                            }
                            autosize
                            minRows={1}
                            maxRows={4}
                            data-testid={`section-scratch-input-${entry.id}`}
                          />
                        </>
                      )}
                    </Stack>
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </Group>
    </FormSection>
  );
};
